<script context="module" lang="ts">
  export const prerender = true;
</script>

<script lang="ts">
  import FlyOutTeachMenu from '$lib/teach/FlyOutTeachMenu.svelte';
  import LicensePricingTabs from '$lib/teach/LicensePricingTabs.svelte';
  import PageTitle from '$lib/teach/resources/PageTitle.svelte';
  import PhilosophyComponent from '$lib/teach/PhilosophyComponent.svelte';
  import PricingTable from '$lib/teach/PricingTable.svelte';
  import { highContrastStore, toggleHighContrastStore } from '$lib/stores/highContrastTheme.store';
  import StudentExperienceCarousel from '$lib/teach/StudentExperienceCarousel.svelte';
  import TestimonialCard from '$lib/teach/TestimonialCard.svelte';
  import LoginModal from '$lib/app/modals/authentication/LoginModal.svelte';
  import RegisterModal from '$lib/app/modals/authentication/RegisterModal.svelte';
  import { closeModal, openModal } from '$lib/stores/modals.store';
  import { goto } from '$app/navigation';
  import {
    onLicensePurchaseRequested,
    onLicenseQuoteRequested,
  } from '$lib/root/LicenseQuotingUtils';

  const debug = false;
  let opacities = [0, 0.25, 1];
  let opacityIndex = 0;
  function toggle() {
    opacityIndex = (opacityIndex + 1) % opacities.length;
    // console.log('toggling');
  }
  const year = new Date().getFullYear();

  let highContrast = false;
  highContrastStore.subscribe((bool) => {
    highContrast = bool;
  });

  function switchTheme() {
    toggleHighContrastStore();
  }

  let links = [
    { href: '#student-experience', text: 'Student Experience' },
    { href: '#teacher-experience', text: 'Teacher Experience' },
    { href: '#testing', text: 'Testing' },
    { href: '/teach/resources', text: 'Resources' },
    { href: '#philosophy', text: 'Philosophy' },
    { href: '#our-team', text: 'Our Team' },
    { href: '#system-requirements', text: 'System Requirements' },
    { href: '#pricing', text: 'Pricing' },
    { href: 'https://info.utheory.com', text: 'Blog' },
  ];

  function loginRegisterRedirect() {
    goto('/app').then(() => {
      closeModal();
    });
  }

  let flyOut1: FlyOutTeachMenu;
  let flyOut2: FlyOutTeachMenu;
  let flyOut3: FlyOutTeachMenu;

  function toggleMenu(evt: Event) {
    if (evt instanceof KeyboardEvent) {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
    }
    flyOut1.toggle();
    flyOut2.toggle();
    flyOut3.toggle();
    evt.preventDefault();
    evt.stopPropagation();
  }

  function hideAllMenus() {
    flyOut1.set(false);
    flyOut2.set(false);
    flyOut3.set(false);
  }
</script>

<LoginModal doNotCloseModal on:loginComplete={loginRegisterRedirect} />
<RegisterModal doNotCloseModal on:registrationComplete={loginRegisterRedirect} />

<PageTitle
  doNotAutoCapitalize
  hideTitle
  title="uTheory for Teachers"
  description={`Learn about teaching with uTheory. uTheory is the most engaging way to 
  teach and learn music theory. uTheory empowers students to become independent
  musicians who read fluently. Learn about teaching with uTheory and create your
  free teacher account.`}
/>

<div class:high-contrast={highContrast}>
  {#if debug}
    <button class="fixed right-0 z-10 btn shadow-lg text-black bg-yellow-400" on:click={toggle}
      >Toggle</button
    >
    <div class="fixed z-10 top-0">
      <span class="bg-yellow-400 px-4 py-2 xs:hidden">Tiny</span>
      <span class="bg-yellow-400 px-4 py-2 hidden xs:block sm:hidden">xs</span>
      <span class="bg-yellow-400 px-4 py-2 hidden sm:block md:hidden">sm</span>
      <span class="bg-yellow-400 px-4 py-2 hidden md:block lg:hidden">md</span>
      <span class="bg-yellow-400 px-4 py-2 hidden lg:block xl:hidden">lg</span>
      <span class="bg-yellow-400 px-4 py-2 hidden xl:block 2xl:hidden">xl</span>
      <span class="bg-yellow-400 px-4 py-2 hidden 2xl:block">2xl</span>
    </div>
  {/if}

  <div style="position: relative">
    <div class="xs:hidden">
      <FlyOutTeachMenu on:hide={hideAllMenus} bind:this={flyOut1} {links} />
    </div>

    <div class="bg-blue wrapper pt-4">
      <header class="">
        <!-- Navigation: Big Screens -->
        <div class="margins md:flex justify-between hidden">
          <div class="flex-initial mt-2">
            <a href="/"
              ><img
                class="inline-block h-14"
                src="/teach/t-landing/logo-utheory-navigation-green.svg"
                alt="uTheory Logo"
                aria-hidden="true"
              /><span class="sr-only">uTheory Homepage</span></a
            >
          </div>
          <div id="nav" class="text-right relative" style="padding-top: 10px">
            <button
              on:click={() => {
                openModal('register');
              }}
              class="text-white font-semibold underline mt-3 pr-3 mr-2 lg:mr-10"
              >Create a Free Teacher Account ></button
            >
            <button
              on:click={() => {
                openModal('login');
              }}
              class="btn-green text-lg mr-4 lg:mr-14">Sign In</button
            >
            <button
              style="background: url(/teach/t-landing/icon-hamburger-menu-lines.svg) no-repeat left; min-width: 33px; min-height: 33px"
              class="relative"
              on:touchstart={toggleMenu}
              on:mousedown={toggleMenu}
              on:keydown={toggleMenu}
            >
              <span class="sr-only">Show/Hide Navigation Menu</span>&nbsp;
            </button>
            <div class="invisible xs:visible">
              <FlyOutTeachMenu
                on:hide={hideAllMenus}
                bind:this={flyOut2}
                class="absolute"
                {links}
              />
            </div>
          </div>
        </div>
        <!-- /Navigation: Big Screens -->

        <!-- /Navigation: Small Screens -->
        <div class="margins block md:hidden">
          <div id="nav" class="flex justify-between relative">
            <button
              on:click={() => {
                openModal('register');
              }}
              class="text-white font-semibold underline text-xs xs:text-base py-4"
              >Create a Free Teacher Account ></button
            >
            <div class="text-right">
              <button
                on:click={() => {
                  openModal('login');
                }}
                class="btn-green text-xs xs:text-base">Sign In</button
              >
            </div>
          </div>
          <hr class="mt-3 mb-5" />
          <div class="margins flex">
            <div class="flex-initial">
              <a href="/"
                ><img
                  class="inline-block w-24"
                  src="/teach/t-landing/logo-utheory-navigation-green.svg"
                  alt="uTheory Logo"
                  aria-hidden="true"
                /></a
              >
            </div>
            <div id="nav" class="text-right flex-1 relative pt-1">
              <button
                style="background: url(/teach/t-landing/icon-hamburger-menu-lines.svg) no-repeat left; min-width: 33px; min-height: 33px"
                on:touchstart={toggleMenu}
                on:mousedown={toggleMenu}
                on:keydown={toggleMenu}
              >
                <span class="sr-only">Show/Hide Menu</span>&nbsp;
              </button>
              <div class="invisible xs:visible">
                <FlyOutTeachMenu
                  on:hide={hideAllMenus}
                  bind:this={flyOut3}
                  class="fixed xs:absolute"
                  {links}
                />
              </div>
            </div>
          </div>
        </div>
      </header>
      <!-- /Navigation: Small Screens -->

      <!-- Hero -->
      <div
        class="flex margins text-white mt-5 md:mt-14 flex-col-reverse flex-wrap md:flex-row md:flex-nowrap"
      >
        <div class="text-center md:text-left pt-10 w-full md:w-1/2">
          <h1 class="text-3xl md:text-4xl lg:text-5xl font-medium">
            The online platform for music theory, rhythm, ear training
          </h1>
          <p class="text-xl lg:text-2xl pt-3 px-6 md:px-0 md:pr-4 font-light">
            Boost your students’ learning with engaging video lessons, individualized practice, and
            immediate feedback.
          </p>
          <button
            class="btn {!highContrast ? 'bg-cyan' : 'bg-gray'} mt-6 py-3 px-7 text-base"
            on:click={() => {
              openModal('register');
            }}>Create a Free Teacher Account</button
          >
        </div>
        <div class="md:w-1/2 px-8 md:px-0 md:pl-14 mx-auto">
          <div class="relative" style="max-width: 453px;">
            <img
              class="relative z-10"
              src="/teach/t-landing/utheory-hero-graphic-transparent.png"
              alt="A student using uTheory"
            />
            <!-- svelte-ignore a11y-media-has-caption -->
            <video
              src="/teach/t-landing/videos/hero-video-2.mp4"
              autoplay
              preload="auto"
              loop
              muted
              playsinline
              alt="Images from the uTheory app"
              class="absolute z-0 transform-gpu -skew-x-12"
              style="top:33%; left:30%; width:56%; --tw-skew-x: -10deg;"
            />
          </div>
        </div>
      </div>

      <!-- Social Proof -->
      <div
        class="mx-auto bg-gray-75 transform translate-y-24 lg:translate-y-28 mb-28 lg:mb-40 md:max-w-screen-xl -mt-4"
      >
        <div class="pt-2 sm:pt-0 pb-4 md:pt-4 md:pb-12 lg:pb-16 lg:pt-8">
          <h2 class="text-4xl font-bold text-center text-green pt-2">uTheory is trusted by</h2>
          <ul class="flex flex-wrap sm:flex-nowrap justify-center flex-row items-center mt-6 mx-2">
            <li class="flex-auto text-center">
              <img
                class=" max-w-full mx-auto"
                src="/teach/t-landing/logo-trusted-oberlin-group.png"
                alt="Oberlin College & Conservatory"
              />
              <span class="sr-only">Oberlin College & Conservatory</span>
            </li>
            <li class="flex-auto text-center">
              <img
                class=" max-w-full mx-auto"
                src="/teach/t-landing/logo-trusted-miami-dade-group.png"
                alt="Miami Dade Public Schools"
              />
              <span class="sr-only">Miami Dade Public Schools</span>
            </li>
            <li class="flex-auto text-center">
              <img
                class=" max-w-full mx-auto"
                src="/teach/t-landing/logo-trusted-baylor-group.png"
                alt="Baylor University"
              />
              <span class="sr-only">Baylor University</span>
            </li>
            <li class="flex-auto text-center">
              <img
                class=" max-w-full mx-auto"
                src="/teach/t-landing/logo-trusted-columbia-group.png"
                alt="Columbia University"
              />
              <span class="sr-only">Columbia University</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Why uTheory? -->
    <div class="wrapper">
      <div class="margins grid grid-cols-1 lg:grid-cols-2  px-4">
        <div>
          <h2 class="text-cyan font-semibold text-5xl pt-3">Why uTheory?</h2>
          <ul class="list-disc mt-5 ml-4 lg:pr-28 text-gray text-xl">
            <li class="pl-3 mt-3">
              Empower your students to become <span class="text-green font-semibold">
                independent musicians</span
              > who read fluently
            </li>
            <li class="pl-3 mt-3">
              <span class="text-green font-semibold">Gain rehearsal time</span> without sacrificing theory
              instruction
            </li>
            <li class="pl-3 mt-3">
              Teach theory more efficiently with <span class="text-green font-semibold"
                >self-directed learning</span
              >
              and <span class="text-green font-semibold">individualized practice</span>
            </li>
            <li class="pl-3 mt-3">
              <span class="text-green font-semibold">Set students up for success</span> in advanced music
              studies
            </li>
          </ul>
        </div>
        <div class="pt-6">
          <div class="embed-container shadow-lg">
            <!-- Use https://embedresponsively.com/ to get responsive code video embed code -->
            <iframe
              style="width: 100%; height: 100%"
              src="https://www.youtube-nocookie.com/embed/8mZk5ma6W_o?controls=0"
              title="YouTube video player"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
          </div>
        </div>
      </div>
    </div>
    <!-- /Why uTheory -->

    <!-- Testimonial #1 -->
    <div class="mt-20">
      <TestimonialCard>
        <svelte:fragment slot="quote">
          <span class="font-black">Thanks a million for uTheory!</span>
          Music theory has never seemed so straight-forward before. It's like a whole new world.
        </svelte:fragment>
        <svelte:fragment slot="personName">Clara Dahmer</svelte:fragment>
        <svelte:fragment slot="schoolName"
          >Oberlin Conservatory Student, Class of 2022</svelte:fragment
        >
      </TestimonialCard>
    </div>

    <!-- Student Experience -->
    <div class="wrapper">
      <div
        class="margins flex flex-wrap md:flex-none md:grid md:grid-cols-2 mt-24"
        id="student-experience"
      >
        <div class="md:pr-8 lg:pr-10 xl:pr-20 self-center mx-auto mb-4 md:text-left text-center">
          <h2 class="text-5xl font-semibold text-blue mb-1">Student Experience</h2>
          <p class="text-gray-light text-xl">
            uTheory is the most engaging way for students to learn music theory.
          </p>
          <button
            on:click={() => {
              openModal('register');
            }}
            class="btn-green mt-8 hidden md:inline-block text-lg"
            >Start Teaching with uTheory</button
          >
        </div>
        <StudentExperienceCarousel
          videos={[
            {
              src: '/teach/t-landing/videos/student-exp-carousel-1.mp4',
              description: 'Music theory, rhythm and ear training',
            },
            {
              src: '/teach/t-landing/videos/student-exp-carousel-2.mp4',
              description: 'Interactive video lessons from top teachers',
            },
            {
              src: '/teach/t-landing/videos/student-exp-carousel-3.mp4',
              description: 'Immediate feedback',
            },
            {
              src: '/teach/t-landing/videos/student-exp-carousel-4.mp4',
              description: 'Adaptive practice based on performance',
            },
          ]}
        />
        <div class="inline-block md:hidden mx-auto">
          <button
            on:click={() => {
              openModal('register');
            }}
            class="btn-green mt-8 text-lg">Start Teaching with uTheory</button
          >
        </div>
      </div>
    </div>
    <!-- /Student Experience -->

    <!-- Teacher Experience -->
    <div class="wrapper bg-cyan py-16 lg:py-24 mt-12">
      <div class="margins bg-white py-20" id="teacher-experience">
        <h2 class="text-cyan text-5xl pb-8 text-center font-semibold">Teacher Experience</h2>
        <div class="teacher-experience left">
          <div>
            <h3>Create and configure classes</h3>
            <p>
              Invite students to join your classes, and set preferences from solfege settings to
              individual accommodations.
            </p>
          </div>
          <div class="img">
            <img
              src="/teach/t-landing/graphic-teacherexp-create-classes.svg"
              alt="A cartoon group of students interacting with a smart board"
            />
          </div>
        </div>
        <div class="teacher-experience right">
          <div>
            <img
              class="mx-auto"
              src="/teach/t-landing/graphic-teacherexp-assign-lessons.svg"
              alt="A cartoon group of students interacting with a smart board"
            />
          </div>
          <div>
            <h3>Assign lessons and practice</h3>
            <p>
              Keep students on track with lesson due dates, assign specific lessons and exercises to
              practice, and hide future content from the student view.
            </p>
          </div>
        </div>
        <div class="teacher-experience left">
          <div>
            <h3>Track student progress</h3>
            <p>
              View an entire class's progress at a glance, or drill down to detailed assessment data
              for individual students.
            </p>
          </div>
          <div>
            <img
              class="mx-auto"
              src="/teach/t-landing/graphic-teacherexp-track-progress.svg"
              alt="Two cartoon teachers view student progress graphs."
            />
          </div>
        </div>
        <div class="teacher-experience right">
          <div>
            <img
              class="mx-auto"
              src="/teach/t-landing/graphic-teacherexp-build-tests.svg"
              alt="A cartoon teacher leans over a clipboard with a pencil, writing a test."
            />
          </div>
          <div>
            <h3>Build custom tests and quizzes</h3>
            <p>
              Choose the topics and types of questions, set your test parameters. uTheory will do
              the hard work of writing and grading the test.
            </p>
          </div>
        </div>
      </div>
    </div>
    <!-- Teacher Experience -->

    <!-- Testimonial #2 -->
    <div class="margins mt-20">
      <TestimonialCard>
        <svelte:fragment slot="quote">
          <span class="font-black">uTheory is fabulous!</span>
          I've been teaching for 34 years and your program is SIMPLY AMAZING!!
        </svelte:fragment>
        <svelte:fragment slot="personName">Darcy Reese</svelte:fragment>
        <svelte:fragment slot="schoolName"
          >Choir Director, Franklin Middle School, Thief River Falls, MN</svelte:fragment
        >
      </TestimonialCard>
    </div>

    <!-- Testing & Assessment -->
    <div class="wrapper">
      <div class="margins flex flex-wrap md:flex-nowrap mt-20" id="testing">
        <div class="w-full sm:w-8/12 md:w-7/12 xl:w-5/12 tests-features mx-auto">
          <h2 class="text-5xl text-blue font-semibold text-center md:text-left">
            Testing &amp; Assessment
          </h2>
          <h3>Design custom tests that fit your teaching needs.</h3>
          <ul
            class="tests-features-list"
            style="list-style-image: url(/teach/t-landing/icon-checkmark-green.svg);"
          >
            <li>Automatic question generation and grading</li>
            <li>Topics aligned to your curriculum</li>
            <li>Online placement tests</li>
            <li>Configurable content, passing requirements and more</li>
          </ul>
          <h3>uTheory tests teach, too.</h3>
          <ul
            class="tests-features-list"
            style="list-style-image: url(/teach/t-landing/icon-checkmark-green.svg);"
          >
            <li>Detailed feedback following each test</li>
            <li>Direct access to relevant practice</li>
            <li>Worry-free retakes</li>
          </ul>
          <a
            rel="external"
            href="https://info.utheory.com/create-tests/"
            class="hidden md:inline-block btn-green mr-9 mt-4 text-lg">Create Your First Test</a
          >
        </div>

        <div class="w-full md:w-5/12 xl:w-7/12 flex flex-col">
          <div class="embed-container order-1 mt-4 md:mt-0 md:order-none">
            <iframe
              src="https://www.youtube.com/embed/e5nO0PUp1DE"
              frameborder="0"
              allowfullscreen
              title="Creating Tests with uTheory"
            />
          </div>
          <hr class="hidden md:block w-full bg-gray mx-auto my-5" style="height:2px;" />
          <p class="text-xl text-gray-light my-4 md:my-0">
            Advanced question generation features provide a new test each time a student takes it,
            with minimal internal repetition. There are literally millions of permutations possible,
            so you can allow retakes without the risk of students memorizing the exam.
          </p>
        </div>
        <div class="inline-block md:hidden mx-auto">
          <a
            rel="external"
            href="https://info.utheory.com/create-tests/"
            class="btn-green text-lg mt-4">Create Your First Test</a
          >
        </div>
      </div>
    </div>
    <!-- /Testing & Assessment -->

    <!-- Testimonial #3 -->
    <div class="margins mt-20">
      <TestimonialCard>
        <svelte:fragment slot="quote">
          <span class="font-black"
            >I can't imagine teaching a music theory fundamentals class without uTheory.</span
          >
          The progression of topics, clear explanations, and the opportunity for students to practice
          at their own pace or mine make uTheory the perfect complement to my lectures, live workshops
          and traditional pencil-and-paper assignments.
        </svelte:fragment>
        <svelte:fragment slot="personName">Joseph Lubben</svelte:fragment>
        <svelte:fragment slot="schoolName"
          >Associate Professor of Music Theory, Oberlin Conservatory</svelte:fragment
        >
      </TestimonialCard>
    </div>

    <!-- Philosophy -->
    <div class="wrapper">
      <div class="margins my-20 px-4 sm:px-16" id="philosophy">
        <h2 class="text-5xl mb-8 text-cyan font-semibold text-center">Philosophy</h2>
        <ul class="pl-2 divide-y-2 divide-gray-300">
          <li>
            <PhilosophyComponent
              title="Sound Pedagogy"
              src="/teach/t-landing/icon-philosophy-sound-pedagogy.svg"
              alt="Book with music notes on the cover"
            >
              Lessons in uTheory are based on decades of experience teaching as well as up-to-date
              knowledge of music theory pedagogy. uTheory’s authors teach at the Oberlin
              Conservatory, James Madison University, and Interlochen Arts Camp. Their work has been
              published in the Journal of Music Theory Pedagogy.
            </PhilosophyComponent>
          </li>
          <li>
            <PhilosophyComponent
              title="Active Learning"
              src="/teach/t-landing/icon-philosophy-active-learning.svg"
              alt="computer screen with talking head"
            >
              Students learn best when they're engaged, and uTheory provides hundreds of interactive
              exercises to give them that experience.
            </PhilosophyComponent>
          </li>
          <li>
            <PhilosophyComponent
              title="Aural Connection"
              src="/teach/t-landing/icon-philosophy-aural-connection.svg"
              alt="computer screen with talking head"
            >
              Students hear music for every question they answer, helping them quickly connect
              theoretical concepts to sound.
            </PhilosophyComponent>
          </li>
          <li>
            <PhilosophyComponent
              title="Mastery Learning"
              src="/teach/t-landing/icon-philosophy-mastery-learning.svg"
              alt="computer screen with talking head"
            >
              Students learn at different paces, and uTheory adapts to that. uTheory tracks progress
              towards mastery of skills, not just whether the questions on a predetermined
              assignment were right or wrong, and uses this to determine whether more practice is
              needed, and what that practice should be.
            </PhilosophyComponent>
          </li>
        </ul>
        <div class="m-auto text-center">
          <button
            on:click={() => {
              openModal('register');
            }}
            class="btn-green text-lg">Explore Teaching with uTheory</button
          >
        </div>
      </div>
    </div>
    <!-- /Philosophy -->

    <!-- About Us/Our Team -->
    <div class="wrapper about-us bg-blue py-12">
      <div class="margins" id="our-team">
        <h2 class="text-5xl font-bold text-center text-white">Our Team</h2>
        <div class="md:flex text-center text-white py-4">
          <div class="px-4 mx-auto mb-8 md:mb-0 md:mx-1 md:w-1/3">
            <img
              class="mx-auto"
              src="/teach/t-landing/image-our-team-gregory-ristow.png"
              alt="Gregory Ristow"
            />
            <h3 class="mt-4 text-2xl mb-2">Gregory Ristow</h3>
            <p class="text-gray-50 text-xl md:text-lg lg:text-xl">
              Founder and lead developer, Greg brings twenty years' experience teaching music theory
              and ear training. He teaches at Oberlin and Interlochen.
            </p>
          </div>
          <div class="px-4 mx-auto mb-8 md:mb-0 md:mx-1 md:w-1/3">
            <img
              class="mx-auto"
              src="/teach/t-landing/image-our-team-david-newman.png"
              alt="David Newman"
            />
            <h3 class="mt-4 text-2xl mb-2">David Newman</h3>
            <p class="text-gray-50 text-xl md:text-lg lg:text-xl">
              Author and developer, David's pedagogical videos have millions of views on YouTube. He
              teaches at James Madison University.
            </p>
          </div>
          <div class="px-4 mx-auto mb-8 md:mb-0 md:mx-1 md:w-1/3">
            <img
              class="mx-auto"
              src="/teach/t-landing/image-our-team-leah-sheldon.png"
              alt="Leah Sheldon"
            />
            <h3 class="mt-4 text-2xl mb-2">Leah Sheldon</h3>
            <p class="text-gray-50 text-xl md:text-lg lg:text-xl">
              As Head of Teacher Engagement, Leah brings her experience teaching music in public
              schools to help teachers use uTheory.
            </p>
          </div>
        </div>
        <div class="text-center">
          <a
            rel="external"
            href="https://info.utheory.com/about-us/"
            class="text-gray-50 font-semibold underline text-lg">About Us ></a
          >
        </div>
      </div>
    </div>
    <!-- About Us/Our Team -->

    <!-- What do my students need? -->
    <div class="wrapper">
      <div class="margins pt-24 mt-2" id="system-requirements">
        <div class="flex flex-wrap md:flex-nowrap">
          <div class="mx-auto order-1 md:order-none md:w-5/12 lg:w-1/2">
            <img
              src="/teach/t-landing/image-devices-needed-mockup.png"
              alt="uTheory running on a phone, tablet and computer"
              class="mt-10"
            />
          </div>
          <div class="mx-auto px-4 md:mx-0 md:px-0 md:pl-20">
            <h2 class="text-4xl text-blue font-bold mx-auto">
              What do my students need to use uTheory?
            </h2>
            <div class="px-8 sm:px-0">
              <ul class="list-disc my-3 ml-4 pr-20 text-gray text-xl pl-12 md:pl-0">
                <li class="pl-2 mt-4">Computer</li>
                <li class="pl-2 mt-4">Chromebook</li>
                <li class="pl-2 mt-4">Phone or Tablet</li>
              </ul>
              <p class="text-gray text-xl">
                uTheory runs in a web browser. No software or app installation required.
              </p>
            </div>
            <div class="block text-center md:text-left">
              <a
                class="btn btn-green mt-8 text-lg"
                rel="external"
                href="https://blog.utheory.com/system-requirements/">See System Requirements</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- End: What do my students need? -->

    <!-- Testimonial #4 -->
    <div class="margins mt-20">
      <TestimonialCard>
        <svelte:fragment slot="quote">
          <span class="font-black">uTheory is a great program.</span>
          The value is excellent and it's easily accessible and user-friendly.
        </svelte:fragment>
        <svelte:fragment slot="personName">Nicole Avesani</svelte:fragment>
        <svelte:fragment slot="schoolName">Avesani Voice Studio</svelte:fragment>
      </TestimonialCard>
    </div>
    <!-- End: Testimonial #4 -->

    <!-- Pricing Table -->
    <div class="wrapper pricing-table mt-14" id="pricing">
      <div class="mx-auto max-w-screen-xl w-full">
        <h2 class="text-cyan text-5xl font-bold text-center mb-4">Pricing</h2>
        <PricingTable />
      </div>
    </div>
    <!-- End Pricing Table -->

    <!-- Pricing Calculator -->
    <div class="wrapper tight">
      <div class="margins mt-24 flex flex-wrap">
        <div class="w-full lg:w-5/12 text-gray px-4 sm:px-0 sm:pr-3 text-xl">
          <h2 class="text-blue text-4xl mb-6 font-bold">uTheory is free for teachers.</h2>
          <p>
            A group license for education gives students in your classes unlimited access to
            uTheory. The costs for an institution start at 75% off the individual subscription rate.
            Group licenses include custom testing features.
          </p>
          <p class="mt-3">Licenses last one year, and can begin on any day of your choosing.</p>
          <p class="mt-3">
            To learn more about using group licenses, check out these
            <a
              rel="external"
              href="https://info.utheory.com/introducing-group-licenses-73e5915e4ba4/"
              class="text-cyan underline font-semibold">frequently asked questions</a
            >
            or
            <a
              rel="external"
              href="/teach/pdfs/utheory-purchasing-guide.pdf"
              class="text-cyan underline font-semibold">download our license purchasing guide.</a
            >
          </p>
        </div>
        <div class="w-full mt-10 lg:mt-0 lg:w-7/12" id="pricing-calculator">
          <LicensePricingTabs
            showActionButtons
            on:quoteRequested={onLicenseQuoteRequested}
            on:purchaseRequested={onLicensePurchaseRequested}
          />
        </div>
      </div>
    </div>
    <!-- End Pricing Calculator -->

    <!-- Contact us/Questions about uTheory -->
    <div class="wrapper mt-24">
      <div class="margins py-6 text-white  bg-green">
        <div class="xs:flex xs:align-top justify-between max-w-2xl mx-auto">
          <div class="px-4 float-left xs:flex">
            <img
              src="/teach/t-landing/graphic-questions-about-utheory-icon.svg"
              aria-hidden="true"
              alt="two talking bubbles with elipses"
              class="self-start"
            />
          </div>
          <div class="px-4 py-2">
            <h2 class="text-right xs:text-left text-3xl font-semibold max-w-xl xl:pr-12">
              Questions about using uTheory? We're here to help.
            </h2>
            <p class="text-lg pt-2 leading-snug">
              Contact us by email at <a href="mailto:hello@utheory.com">hello@utheory.com</a> or by
              phone
              <a href="tel:8668380556">(866) 838 0556</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
    <!-- End Contact us/Questions about uTheory -->

    <!-- Closing Tag / uTheory Does it All & Footer -->
    <div class="wrapper mt-8 p-12 bg-blue text-white text-center">
      <div class="margins">
        <img
          src="/teach/t-landing/logo-utheory-green-square.jpg"
          alt="uTheory Logo"
          class="mx-auto"
        />
        <p class="max-w-lg mx-auto mt-4 text-xl xs:text-2xl">
          uTheory does it all: planning, teaching, assessing and grading so your students can learn,
          practice and master music theory
        </p>
      </div>

      <!-- Footer -->
      <div class="margins">
        <div class="h-1 mt-20 mx-auto border-b-2 border-gray-50 rounded-sm" />
        <!-- <hr class="w-5/6 bg-white mx-auto my-5" style="height:2px;" /> -->
        <ul
          class="grid grid-cols-1 xs:grid-cols-2 justify-center md:grid-cols-4 md:justify-between text-gray-50"
        >
          <li class="footer-item">
            <a href="/privacy">Privacy Policy &<br />Terms of Use</a>
          </li>
          <li class="footer-item">
            <a rel="external" href="https://info.utheory.com/accessibility-roadmap-b5e1926d7e3a/"
              >Accessibility</a
            >
          </li>
          <li class="footer-item">
            <button on:click={switchTheme}>Use or disable high-contrast theme</button>
          </li>
          <li class="footer-item">
            <a href="mailto:hello@utheory.com">hello@utheory.com</a><br />
            <a href="tel:18668380556">(866) 838-0556</a>
          </li>
        </ul>
      </div>
      <!-- End Footer -->
    </div>
    <!-- End Closing Tag / uTheory Does it All & Footer -->

    <!-- Copyright & TM -->
    <div class="text-center text-gray-light mx-auto text-base mt-4">
      &copy;{year} uTheory, LLP<br />
      uTheory is a registered trademark of uTheory, LLP
    </div>
    <!-- Copyright & TM -->
  </div>
</div>

<style lang="postcss">
  @import 'https://fonts.googleapis.com/css?family=Roboto:400,300,700,500';

  :global(body) {
    letter-spacing: 0 1em;
  }

  .wrapper.tight {
    @apply lg:px-16 md:px-10 sm:px-4 px-0;
  }

  .wrapper.pricing-table {
    @apply xl:px-16 lg:px-10 px-0;
  }

  .wrapper.about-us {
    @apply lg:px-16 md:px-0;
    width: 100%;
  }

  .embed-container {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    max-width: 100%;
  }
  .embed-container iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .teacher-experience {
    @apply flex items-center px-8 md:px-14 lg:px-20 mb-10 md:mb-8 text-gray;
  }

  .teacher-experience h3 {
    @apply text-blue text-3xl font-semibold mb-2;
    line-height: 36px;
    letter-spacing: 0.28px;
  }

  .teacher-experience p {
    @apply text-xl;
  }
  .teacher-experience div:nth-of-type(1) {
    @apply xs:mr-4 md:w-1/2;
  }

  .teacher-experience div:nth-of-type(2) {
    @apply xs:ml-4 md:w-1/2;
  }

  .teacher-experience.left > div:nth-of-type(2) {
    @apply hidden xs:flex px-4 md:px-12 lg:px-20;
  }

  .teacher-experience.right > div:nth-of-type(1) {
    @apply hidden xs:flex px-4 md:px-12 lg:px-20;
  }

  .teacher-experience img {
    @apply max-w-full mx-auto;
  }

  .tests-features h3 {
    @apply text-2xl mb-3 mt-4 leading-8 font-semibold text-gray;
  }

  .tests-features ul {
    @apply pl-4;
  }

  .tests-features li {
    @apply text-xl text-gray pl-3 mb-2;
  }

  .footer-item {
    @apply w-36 mx-auto mt-4;
    /* max-width: 180px;
		min-width: 120px; */
  }
</style>
